import { Injectable, inject } from '@angular/core';
import { environment } from 'environments/environment';
import * as signalR from '@microsoft/signalr';
import { App } from '@capacitor/app';
import { firstValueFrom } from 'rxjs';
import { AuthService } from '@auth0/auth0-angular';
import { PluginListenerHandle } from '@capacitor/core';

@Injectable({ providedIn: 'root' })
export class SignalRService {
  private readonly authService = inject(AuthService);

  private hubConnection: signalR.HubConnection;
  private registeredEventListeners: string[] = [];
  private onAppStateChangeListener: PluginListenerHandle;

  async initializeConnectionHub(): Promise<void> {
    // Tear down a previous hub (if any) so its app state listener doesn't keep driving the new connection.
    this.cleanUpAppStateChangeListener();
    this.buildConnectionHub();
    await this.startConnection();
    await this.setAppStateListeners();
  }

  on<T>(eventName: string, handler: (data: T) => void): void {
    if (!this.hubConnection) {
      throw new Error('Hub connection is not initialized');
    }

    if (!this.registeredEventListeners.includes(eventName)) {
      this.hubConnection.on(eventName, handler);
      this.registeredEventListeners.push(eventName);
    }
  }

  cleanUpAppStateChangeListener(): void {
    if (this.onAppStateChangeListener) {
      this.stopConnection();
      this.onAppStateChangeListener.remove();
      this.onAppStateChangeListener = null;
    }
  }

  private buildConnectionHub(): void {
    this.hubConnection = new signalR.HubConnectionBuilder()
      .withUrl(`${environment.url.api}/hubs/communication?subscription-key=${environment.ocpApimSubscriptionKey}`, {
        accessTokenFactory: () => firstValueFrom(this.authService.getAccessTokenSilently()),
      })
      .withAutomaticReconnect()
      .build();
    // Handlers are bound to a connection instance, so a fresh hub starts without any.
    this.registeredEventListeners = [];
  }

  private startConnection(): Promise<void> {
    return this.hubConnection.start();
  }

  private stopConnection(): Promise<void> {
    return this.hubConnection.stop();
  }

  private async setAppStateListeners(): Promise<void> {
    this.onAppStateChangeListener = await App.addListener('appStateChange', async ({ isActive }) => {
      if (isActive) {
        // Only (re)start when the hub is actually disconnected. Calling stop() while the
        // app is foregrounded would kill a healthy connection, and automatic reconnect
        // does not resume after an explicit stop().
        if (this.hubConnection.state === signalR.HubConnectionState.Disconnected) {
          this.startConnection();
        }
      } else if (this.hubConnection.state === signalR.HubConnectionState.Connected) {
        // Only tear down a live connection on background. Stopping while Connecting /
        // Reconnecting / Disconnecting aborts negotiation ("stopped during negotiation").
        this.stopConnection();
      }
    });
  }
}
